import { useEffect, useRef } from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { usePlatformEvents } from '../contexts/PlatformEventContext';
import {
  ME_LIST_KEY,
  ME_INFINITE_KEY,
  ME_INVALIDATE_PREFIX,
} from './useConversations';
import type { PaginatedResponse, Conversation } from '../types';

/**
 * Live conversation-name updates. The backend auto-names a fresh conversation
 * and emits `entity.conversation.named` over the fan-out socket; this bridge
 * patches every react-query cache that renders the name, in place, from the
 * event payload (the `useVfsFiles` idiom — no per-event invalidation):
 *
 *  - `ME_LIST_KEY`      — operator sidebar / navbar dropdown list
 *  - `ME_INFINITE_KEY`  — operator ChatList pages (+ first-page `pinned`)
 *  - `['space', *, 'conversations', <id>]`        — header title / meta
 *  - `['space', *, 'conversations', 'list', *]`   — console list page
 *
 * The event carries no spaceId, so the space-scoped keys are matched by
 * predicate. On WS reconnect the `/me` conversations subtree is invalidated
 * once to catch names missed while disconnected.
 *
 * Mount ONCE per PlatformEventProvider via `ConversationNamedEventBridge`
 * (see App.tsx) — this hook's first life ended as dead code because it was
 * mounted nowhere and hardcoded stale keys.
 */

type MeConversationsPage = PaginatedResponse<Conversation> & {
  pinned?: Conversation[];
};

/** Rename in place; returns the ORIGINAL array when nothing matched so
 *  untouched caches keep referential equality (no spurious re-renders). */
function renameRows(
  rows: Conversation[] | undefined,
  conversationId: string,
  name: string,
): Conversation[] | undefined {
  if (!rows?.some((c) => c.conversationId === conversationId && c.name !== name)) {
    return rows;
  }
  return rows.map((c) => (c.conversationId === conversationId ? { ...c, name } : c));
}

export function useConversationNamedEvent() {
  const { subscribe, isConnected } = usePlatformEvents();
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = subscribe(
      [{ eventType: 'entity.conversation.named' }],
      (event) => {
        const { conversationId, name } = (event.data ?? {}) as {
          conversationId?: string;
          name?: string;
        };
        if (!conversationId || !name) return;

        // Operator sidebar / navbar list.
        queryClient.setQueryData<MeConversationsPage>(ME_LIST_KEY, (old) => {
          if (!old) return old;
          const items = renameRows(old.items, conversationId, name);
          const pinned = renameRows(old.pinned, conversationId, name);
          if (items === old.items && pinned === old.pinned) return old;
          return { ...old, items: items ?? old.items, ...(pinned ? { pinned } : {}) };
        });

        // Operator ChatList (infinite pages; complete pinned set rides page 0).
        queryClient.setQueryData<InfiniteData<MeConversationsPage>>(
          ME_INFINITE_KEY,
          (old) => {
            if (!old) return old;
            let changed = false;
            const pages = old.pages.map((page) => {
              const items = renameRows(page.items, conversationId, name);
              const pinned = renameRows(page.pinned, conversationId, name);
              if (items === page.items && pinned === page.pinned) return page;
              changed = true;
              return { ...page, items: items ?? page.items, ...(pinned ? { pinned } : {}) };
            });
            return changed ? { ...old, pages } : old;
          },
        );

        // Space-scoped detail objects (header title / conversation meta). The
        // event has no spaceId — match by key shape.
        queryClient.setQueriesData<Conversation>(
          {
            predicate: (q) =>
              q.queryKey[0] === 'space' &&
              q.queryKey[2] === 'conversations' &&
              q.queryKey[3] === conversationId,
          },
          (old) => (old && old.name !== name ? { ...old, name } : old),
        );

        // Space-scoped console lists.
        queryClient.setQueriesData<PaginatedResponse<Conversation>>(
          {
            predicate: (q) =>
              q.queryKey[0] === 'space' &&
              q.queryKey[2] === 'conversations' &&
              q.queryKey[3] === 'list',
          },
          (old) => {
            if (!old) return old;
            const items = renameRows(old.items, conversationId, name);
            return items === old.items ? old : { ...old, items: items ?? old.items };
          },
        );
      },
    );

    return unsubscribe;
  }, [subscribe, queryClient]);

  // Reconnect catch-up: names emitted while the socket was down were never
  // patched — refetch the /me conversations subtree once per reconnect.
  const wasConnectedRef = useRef(isConnected);
  useEffect(() => {
    const wasConnected = wasConnectedRef.current;
    wasConnectedRef.current = isConnected;
    if (!wasConnected && isConnected) {
      void queryClient.invalidateQueries({ queryKey: ME_INVALIDATE_PREFIX });
    }
  }, [isConnected, queryClient]);
}

/** Render-nothing mount point — keeps the single central mount visible in
 *  App.tsx instead of hiding the subscription inside some page. */
export function ConversationNamedEventBridge(): null {
  useConversationNamedEvent();
  return null;
}
